import type { JcRole, JcProductRole, JcUsageRoleOption, JcConfiguratorFilterOptionSetting } from "@/types/jcFilterSystem"

// Offline fallback only, 1:1 with the backend UsageRole::ALL. Which roles exist
// is catalog data — the admin UI fetches them from GET /backend/usage-roles
// (useJcUsageRolesStore) and this list is used only until that endpoint answers.
// Never treat it as the complete set: roles added in the catalog appear here at
// runtime without a code change, which is the whole point.
export const DEFAULT_JC_ROLES: JcRole[] = ["player", "goalkeeper", "coach", "libero"]

// Roles are open-ended, so this is a shape check, not a membership test — the
// same slug rule the backend applies when it stores a role key. To decide
// whether a key is a role *that exists*, check it against the catalog instead
// (see isKnownRole); this only rejects values that could never be one.
export function isJcRole(value: unknown): value is JcRole {
  return typeof value === "string" && /^[a-z0-9][a-z0-9_-]*$/.test(value)
}

/** True when `value` is one of the roles the catalog actually serves. */
export function isKnownRole(value: unknown, knownRoles: readonly JcRole[]): value is JcRole {
  return typeof value === "string" && knownRoles.includes(value)
}

/**
 * Human-readable label for a role. Precedence: the admin snippet when the CMS
 * actually has it, then the catalog's own translated name, then the raw key.
 *
 * Roles added after the admin snippets were written (libero) have no snippet —
 * without the `label` fallback the UI would show the bare key. Checking the
 * snippet first keeps the existing, properly localized admin wording for the
 * legacy roles instead of overriding it with the catalog name.
 */
export function resolveRoleLabel(role: JcRole, roles: readonly JcUsageRoleOption[], i18n: { t: (key: string) => string; te: (key: string) => boolean }): string {
  const option = roles.find((r) => r.role === role)
  const snippet = option?.labelSnippet || `eecom-teamportal.jerseyConfigurator.usageRole.${role}`
  if (i18n.te(snippet)) return i18n.t(snippet)
  return option?.label || role
}

/**
 * Normalize a product's roles into a clean, de-duplicated string array.
 * Accepts the `displayGroups` association (DAL child shape `[{ role }]`), a plain
 * string array, or — for not-yet-migrated products — derives from the
 * deprecated `usageRole` / `goalkeeper` fields ("universal" → both).
 *
 * `knownRoles` must be the catalog's role set (usageRolesStore.roleValues):
 * `displayGroups` is one flat list mixing role and garment keys, so membership in
 * the catalog is the only thing separating them.
 */
export function normalizeProductRoles(
  product: { displayGroups?: Array<JcProductRole | string> | null; usageRole?: string | null; goalkeeper?: boolean | null },
  knownRoles: readonly JcRole[] = DEFAULT_JC_ROLES
): JcRole[] {
  const fromRoles = (product?.displayGroups ?? []).map(displayGroupKey).filter((key): key is JcRole => isKnownRole(key, knownRoles))
  if (fromRoles.length > 0) return Array.from(new Set(fromRoles))

  // Backwards-compat: derive from the deprecated single-valued usageRole / goalkeeper.
  if (product?.usageRole === "universal") return ["player", "goalkeeper"]
  if (product?.usageRole === "goalkeeper" || product?.goalkeeper) return ["goalkeeper"]
  if (product?.usageRole === "player") return ["player"]
  return []
}

/**
 * Clean a per-category role-exception map (`{ filterOptionId: [role, ...] }`).
 *
 * Entries with an empty list are dropped rather than kept: downstream an empty
 * list reads as "matches no role" and would hide the product in that category,
 * whereas an emptied field means "no exception, use the product's global roles".
 * That is also exactly what the backend stores, so a cleaned map round-trips.
 *
 * `allowedOptionIds`, when given, drops exceptions for options the product is no
 * longer tagged with — those rows do not exist after a replace anyway.
 */
export function sanitizeRoleExceptions(raw: Record<string, JcRole[]> | null | undefined, allowedOptionIds?: readonly string[]): Record<string, JcRole[]> {
  const allowed = allowedOptionIds ? new Set(allowedOptionIds) : null
  const out: Record<string, JcRole[]> = {}
  for (const [optionId, roles] of Object.entries(raw ?? {})) {
    if (allowed && !allowed.has(optionId)) continue
    const cleaned = Array.from(new Set((roles ?? []).filter(isJcRole)))
    if (cleaned.length > 0) out[optionId] = cleaned
  }
  return out
}

/** Shallow equality for two role-exception maps — used to skip no-op saves. */
export function sameRoleExceptions(a: Record<string, JcRole[]>, b: Record<string, JcRole[]>): boolean {
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  return keysA.every((key) => {
    const rolesB = b[key]
    if (!rolesB || rolesB.length !== a[key].length) return false
    const setB = new Set(rolesB)
    return a[key].every((role) => setB.has(role))
  })
}

/**
 * Resolve the effective per-role minimum quantities for a filter option setting.
 * Prefers the new `minRoleQuantities` map; falls back to the deprecated
 * min*Quantity columns for legacy rows that were never backfilled.
 */
export function resolveMinRoleQuantities(
  setting: Pick<JcConfiguratorFilterOptionSetting, "minRoleQuantities" | "minPlayerQuantity" | "minGoalkeeperQuantity"> | undefined | null
): Record<string, number> {
  const map = setting?.minRoleQuantities
  if (map && Object.keys(map).length > 0) {
    const cleaned: Record<string, number> = {}
    for (const [role, value] of Object.entries(map)) {
      if (isJcRole(role) && Number.isInteger(value) && value > 0) cleaned[role] = value
    }
    return cleaned
  }
  // Legacy fallback (only player & goalkeeper had columns).
  const fallback: Record<string, number> = {}
  if ((setting?.minPlayerQuantity ?? 0) > 0) fallback.player = setting!.minPlayerQuantity as number
  if ((setting?.minGoalkeeperQuantity ?? 0) > 0) fallback.goalkeeper = setting!.minGoalkeeperQuantity as number
  return fallback
}

/** Sum of all per-role minimums — the derived total minimum quantity. */
export function totalMinQuantity(map: Record<string, number>): number {
  return Object.values(map).reduce((sum, n) => sum + (n > 0 ? n : 0), 0)
}

/**
 * Build the DAL child payload for a product's `displayGroups` association:
 * `[{ id?, displayGroup }]`.
 *
 * A flat list of key strings is NOT enough here. Only the per-product route
 * (JcAssortmentProductRoute::normalizeDisplayGroups) reshapes plain strings; the
 * products page saves through the configurator route, which hands the payload
 * straight to the DAL — and the DAL rejects a string where it wants a child
 * record ("Expected data to be array.").
 *
 * The existing row id is reused for every key the product already carries, so
 * the write becomes an update: `(product_id, display_group)` is unique, and a
 * fresh insert would collide on the second save. New keys go without an id.
 */
export function buildDisplayGroupWritePayload(product: { displayGroups?: Array<JcProductRole | string> | null }, keys: readonly string[]): Array<{ id?: string; displayGroup: string }> {
  const existingIdByKey: Record<string, string> = {}
  for (const entry of product?.displayGroups ?? []) {
    if (!entry || typeof entry !== "object" || !entry.id) continue
    const key = displayGroupKey(entry)
    if (key) existingIdByKey[key] = entry.id
  }
  return keys.map((key) => (existingIdByKey[key] ? { id: existingIdByKey[key], displayGroup: key } : { displayGroup: key }))
}

/**
 * Row ids of a product's `displayGroups` children that have to be DELETED for the
 * product to end up with exactly `keys`.
 *
 * The configurator route hands the nested payload straight to the DAL, which only
 * adds/updates the children it is given — a key left out of `displayGroups` keeps
 * its row. Swapping one garment for another would therefore leave both rows behind
 * and the old one would win on the next read. The route takes the rows to drop as
 * a top-level `deletedDisplayGroups` and deletes them before the upsert, the same
 * pattern as `deletedProperties` / `deletedLanguages`.
 *
 * `managedKeys` is what this UI owns — the role catalog plus the garment catalog.
 * A row with any other key is left alone: it belongs to a kind the products page
 * does not edit, or a catalog that failed to load, and dropping it would silently
 * lose an assignment.
 */
export function collectRemovedDisplayGroupIds(product: { displayGroups?: Array<JcProductRole | string> | null }, keys: readonly string[], managedKeys: readonly string[]): string[] {
  const keep = new Set(keys)
  const managed = new Set(managedKeys)
  const ids: string[] = []
  for (const entry of product?.displayGroups ?? []) {
    if (!entry || typeof entry !== "object" || !entry.id) continue
    const key = displayGroupKey(entry)
    if (!key || keep.has(key) || !managed.has(key)) continue
    ids.push(entry.id)
  }
  return ids
}

/**
 * Read the raw key of a `displayGroups` entry — either a bare string key or the
 * DAL-child object. The backend column is `display_group`, so the DAL serializes
 * the key as `displayGroup`; `role` is accepted as a legacy alias. Garment keys
 * live in the same column as roles now that the product carries one flat, mixed
 * key list.
 */
function displayGroupKey(entry: JcProductRole | string | { displayGroup?: string; role?: string } | null | undefined): string | undefined {
  if (typeof entry === "string") return entry
  const child = entry as { displayGroup?: string; role?: string } | null
  return child?.displayGroup ?? child?.role
}

/**
 * Pull a product's assigned garment key out of its mixed `displayGroups` list.
 * Only keys present in the catalog's garment set (kind="garment") qualify, so
 * role keys are never mistaken for a garment. Returns the first match or null.
 */
export function normalizeProductGarment(product: { displayGroups?: Array<JcProductRole | string> | null }, garmentKeys: string[]): string | null {
  const garments = new Set(garmentKeys)
  for (const entry of product?.displayGroups ?? []) {
    const key = displayGroupKey(entry)
    if (key && garments.has(key)) return key
  }
  return null
}

/**
 * Build the flat `displayGroups` write payload — a de-duplicated list of plain
 * string keys mixing usage roles and (optionally) one garment key. The backend
 * resolves each key to its catalog entry and reconciles the product's
 * associations server-side.
 */
export function buildDisplayGroupKeys(roles: JcRole[], garmentKey?: string | null): string[] {
  const keys: string[] = [...roles]
  if (garmentKey) keys.push(garmentKey)
  return Array.from(new Set(keys))
}
